import * as yup from 'yup';

import { validateDate } from 'app/utils/validations';
import { parseCurrencyIntoFloat } from 'app/utils/strings';
import { calculateMinimumPeriodStartDate, isDateBefore } from 'app/utils/date';

// Step 1 - Define Calendar

// Requires name
// Requires payFrequency of MONTHLY, 4-WEEKLY, FORTNIGHTLY, WEEKLY

export const payCalendarDefineCalendarFormSchema = yup.object().shape({
  name: yup
    .string()
    .required('Pay calendar name is mandatory')
    .min(3, 'Please use a minimum of 3 characters for the name'),
  externalPayCalendarId: yup.string(),
  payFrequency: yup.string().required('Pay frequency is mandatory'),
});

// Step 2 - Pay Day Information

// LAST_DAY_OF_PERIOD = 'Last day of the period', // monthly, 4-weekly, fortnightly, weekly
// LAST_BUSINESS_DAY = 'Last business day of the period', // monthly
// LAST_CUSTOM_WEEKDAY = 'Last custom weekday of the period', // monthly
// N_BUSINESS_DAY_OF_THE_PERIOD = 'Nth business day of the period', // monthly, 4-weekly, fortnightly, weekly
// N_DAY_OF_PERIOD = 'Nth day of the period', // monthly, 4-weekly, fortnightly, weekly
// FIXED_DAY = 'Fixed day', // monthly

export const payCalendarPaydayInformationFormSchema = yup.object().shape({
  payType: yup.string().required('Pay type is mandatory'),
  payday: yup
    .string()
    .when('payFrequency', (payFrequency, schema) => {
      if (payFrequency.toString() === 'MONTHLY') {
        return schema.oneOf(
          [
            'LAST_DAY_OF_PERIOD',
            'LAST_BUSINESS_DAY',
            'LAST_CUSTOM_WEEKDAY',
            'N_BUSINESS_DAY_OF_PERIOD',
            'N_DAY_OF_PERIOD',
            'FIXED_DAY',
            'CUSTOM_DAY', // backwards compatibility
            'CUSTOM_DAY_IN_ARREARS', // backwards compatibility
            'FIRST_BUSINESS_DAY', // backwards compatibility
            'FIRST_CUSTOM_WEEKDAY', // backwards compatibility
          ],
          'Select a valid option for monthly pay frequency'
        );
      }
      if (['FOUR_WEEKLY', 'FORTNIGHTLY', 'WEEKLY'].includes(payFrequency.toString())) {
        return schema.oneOf(
          ['LAST_DAY_OF_PERIOD', 'LAST_BUSINESS_DAY', 'N_DAY_OF_PERIOD'],
          'Select a valid option for weekly, fortnightly, or 4-weekly pay frequency'
        );
      }
      return schema;
    })
    .required('Payday is mandatory'),
  paydayFixedDay: yup.string().when('payday', {
    is: 'FIXED_DAY',
    then: (schema) =>
      schema
        .required('You must provide a valid date in the form dd/mm/yyyy')
        .test('Invalid date', 'Enter a valid date', (value) => !value || validateDate(value))
        // eslint-disable-next-line func-names
        .test('start-date-within-limit', function (value) {
          const { startDate } = this.parent;
          if (!value || !startDate) return true; // if missing, let required or other tests handle it

          // If start date is before the calculated limit date, return an error.
          if (isDateBefore(value, startDate)) {
            const warning = `Your start date is too early, please set to ${startDate} as a minimum`;

            return this.createError({
              message: warning,
            });
          }

          return true;
        }),
  }),
  startDate: yup
    .string()
    .required('Start date (dd/mm/yyyy) is mandatory')
    .test('Invalid date', 'Enter a valid date', (value) => !value || validateDate(value))
    // eslint-disable-next-line func-names
    .test('start-date-within-limit', function (value) {
      // Access sibling field: payFrequency
      const { payFrequency } = this.parent;
      if (!value || !payFrequency) return true; // if missing, let required or other tests handle it

      let startDateIsBeforePeriodStart = false;
      let startDateIsBeforePeriodStartWarning = '';

      const calculatedMinimumPeriodStart = calculateMinimumPeriodStartDate(payFrequency);
      startDateIsBeforePeriodStart = isDateBefore(value, calculatedMinimumPeriodStart);
      startDateIsBeforePeriodStartWarning = `Your start date is too early, please set to ${calculatedMinimumPeriodStart} as a minimum`;

      // If start date is before the calculated limit date, return an error.
      if (startDateIsBeforePeriodStart) {
        return this.createError({
          message: startDateIsBeforePeriodStartWarning,
        });
      }

      return true;
    })
    // eslint-disable-next-line func-names
    .test('monthly-start-date-max-day', function (value) {
      const { payFrequency } = this.parent;
      if (!value || payFrequency !== 'MONTHLY') return true;

      const day = new Date(value).getUTCDate();
      if (day > 28) {
        return this.createError({
          message: 'For monthly calendars, the start date must be on or before the 28th',
        });
      }

      return true;
    }),
  paydayNDayOfPeriod: yup.number().when('payday', {
    is: 'N_DAY_OF_PERIOD',
    then: (schema) =>
      schema
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .positive('Please provide a postive number value from 1 to 31')
        .required('Please provide a value from 1 to 31')
        .integer('Please only use whole number values from 1 to 31')
        .min(1, 'Please use a value from 1 to 31')
        .max(31, 'Please use a value from 1 to 31'),
  }),
  paydayNBusinessDayOfPeriod: yup.number().when('payday', {
    is: 'N_BUSINESS_DAY_OF_PERIOD',
    then: (schema) =>
      schema
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .positive('Please provide a postive number value from 1 to 23')
        .required('Please provide a value from 1 to 23')
        .integer('Please only use whole number values from 1 to 23')
        .min(1, 'Please use a value from 1 to 23')
        .max(23, 'Please use a value from 1 to 23'),
    otherwise: (schema) => schema.notRequired(),
  }),
  paydayCustomDay: yup.number().when('payday', (payday, schema) => {
    if (['CUSTOM_DAY', 'CUSTOM_DAY_IN_ARREARS'].includes(payday.toString())) {
      return schema
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .required('Please provide a value from 1 to 31')
        .integer('Please only use whole number values from 1 to 31')
        .min(1, 'Please use a value from 1 to 31')
        .max(31, 'Please use a value from 1 to 31');
    }
    return schema.notRequired();
  }),
});

// Step 3 - Calendar defaults
export const payCalendarCalendarDefaultsFormSchema = yup.object().shape({
  workingDays: yup.array().min(1, 'You need to select one or more weekdays'),
  paydayNonBusinessDayFailover: yup.string().required('You need to choose a default payday action'),
  location: yup.string().required('Location is mandatory'),
  defaultAnnualGrossSalary: yup
    .number()
    .transform((_, originalValue) => {
      const parsedSalary = parseCurrencyIntoFloat(originalValue);
      return Number.isNaN(parsedSalary) ? undefined : parsedSalary;
    })
    .min(0, 'Salary must be a positive number')
    .max(500000, 'The salary must not exceed £500,000'), // Upper bound check
});

// Step 4 - Cut-off Information
export const createPayCalendarCutoffInformationFormSchema = (
  payday?: string,
  paydayNDayOfPeriod?: number,
  paydayNBusinessDayOfPeriod?: number
) =>
  yup.object().shape({
    payrollCutoff: yup
      .string()
      .when('payFrequency', (payFrequency, schema) => {
        if (payFrequency.toString() === 'MONTHLY') {
          return schema.oneOf(
            [
              'N_DAYS_BEFORE_PAYDAY',
              'N_BUSINESS_DAYS_BEFORE_PAYDAY',
              'N_BUSINESS_DAY_OF_PERIOD',
              'N_DAY_OF_PERIOD',
              'FIXED_DAY',
            ],
            'Select a valid option for monthly pay frequency'
          );
        }
        if (['FOUR_WEEKLY', 'FORTNIGHTLY', 'WEEKLY'].includes(payFrequency.toString())) {
          return schema.oneOf(
            ['N_DAYS_BEFORE_PAYDAY', 'N_BUSINESS_DAYS_BEFORE_PAYDAY', 'N_DAY_OF_PERIOD'],
            'Select a valid option for weekly, fortnightly, or 4-weekly pay frequency'
          );
        }
        return schema;
      })
      .required('Payroll Cutoff day is mandatory'),
    payrollCutoffDays: yup
      .number()
      .transform((value) => (Number.isNaN(value) ? undefined : value))
      .when(['payrollCutoff', 'payFrequency'], (values, schema) => {
        const [payrollCutoff, payFrequency] = values as [string, string];

        if (payrollCutoff === 'FIXED_DAY') {
          // Disable numeric validation when FIXED_DAY is selected, as it requires a date input instead
          return schema.strip();
        }

        let max = 31;
        const min = 0;

        if (payFrequency === 'MONTHLY') {
          max =
            payrollCutoff === 'N_BUSINESS_DAYS_BEFORE_PAYDAY' ||
            payrollCutoff === 'N_BUSINESS_DAY_OF_PERIOD'
              ? 23
              : 31;
        } else if (payFrequency === 'FOUR_WEEKLY') {
          max = payrollCutoff === 'N_BUSINESS_DAYS_BEFORE_PAYDAY' ? 20 : 28;
        } else if (payFrequency === 'FORTNIGHTLY') {
          max = payrollCutoff === 'N_BUSINESS_DAYS_BEFORE_PAYDAY' ? 10 : 14;
        } else if (payFrequency === 'WEEKLY') {
          max = payrollCutoff === 'N_BUSINESS_DAYS_BEFORE_PAYDAY' ? 5 : 7;
        }

        const rangeMsg = `${min} to ${max}`;

        let result = schema
          .required(`Please provide a value from ${rangeMsg}`)
          .min(min, `Please use a value from ${rangeMsg}`)
          .max(max, `Please use a value from ${rangeMsg}`)
          .integer(`Please only use whole number values from ${rangeMsg}`);

        if (
          payrollCutoff === 'N_DAY_OF_PERIOD' &&
          payday === 'N_DAY_OF_PERIOD' &&
          paydayNDayOfPeriod !== undefined
        ) {
          result = result.test(
            'cutoff-before-payday',
            `Cut-off day cannot be on or after Pay Day (day ${paydayNDayOfPeriod})`,
            (value) => value === undefined || value < paydayNDayOfPeriod
          );
        }

        if (
          payrollCutoff === 'N_BUSINESS_DAY_OF_PERIOD' &&
          payday === 'N_BUSINESS_DAY_OF_PERIOD' &&
          paydayNBusinessDayOfPeriod !== undefined
        ) {
          result = result.test(
            'cutoff-before-payday',
            `Cut-off day cannot be on or after Pay Day (business day ${paydayNBusinessDayOfPeriod})`,
            (value) => value === undefined || value < paydayNBusinessDayOfPeriod
          );
        }

        return result;
      }),
    payrollCutoffFixedDay: yup.string().when('payrollCutoff', {
      is: 'FIXED_DAY',
      then: (schema) =>
        schema
          .required('You must provide a valid date in the form dd/mm/yyyy')
          .test('Invalid date', 'Enter a valid date', (value) => !value || validateDate(value)),
      // Make sure to disable validation when payrollCutoff isn't 'FIXED_DAY'
      otherwise: (schema) => schema.notRequired(),
    }),
    cutOffNonBusinessDayFailover: yup
      .string()
      .required('You need to choose a default cut-off action'),
  });

// Step 5 - Aslan Details
export const payCalendarAslanDetailsFormSchema = (
  isReserveEnabled: boolean,
  isOnDemandPayEnabled: boolean
) =>
  yup
    .object()
    .shape({
      odpAllowancePercentage: yup.number().when([], {
        is: () => isOnDemandPayEnabled,
        then: (schema) => schema.required('On-demand pay sub-limit is mandatory'),
        otherwise: (schema) => schema.notRequired(),
      }),

      startingBalanceDays: yup.number().required('1st of month accrual is mandatory'),

      customOdpAllowancePercentage: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .when('odpAllowancePercentage', {
          is: 200, // When odpAllowancePercentage is 200
          then: (schema) =>
            schema
              .required('Value is required when choosing a Custom %')
              .min(0, 'Please use a value from 0 to 100')
              .max(100, 'Please use a value from 0 to 100')
              .integer('Please provide a whole number value from 0 to 100'),
          otherwise: (schema) => schema.notRequired(),
        }),

      globalAllowancePercentage: yup
        .number()
        .integer('Global limit must be an integer')
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .required('Global allowance is required')
        .test(
          'valid-global-allowance-percentage',
          'Global limit must be between 1 and 100',
          function validateGlobalAllowancePercentage(value) {
            // If it's 200 (Custom %), it's valid (the custom value will be validated separately)
            if (value === 200) {
              return true;
            }
            // For preset values, check if it's within the allowed range (0 is not allowed)
            return value !== undefined && value >= 1 && value <= 100;
          }
        ),

      customGlobalAllowancePercentage: yup
        .number()
        .integer('Custom Global limit must be an integer')
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .when('globalAllowancePercentage', {
          is: 200, // When globalAllowancePercentage is 200 (Custom %)
          then: (schema) =>
            schema
              .required('Custom Global limit is required when choosing Custom %')
              .min(1, 'Global limit must be between 1 and 100')
              .max(100, 'Global limit must be between 1 and 100')
              .integer('Please provide a whole number value'),
          otherwise: (schema) => schema.notRequired(),
        }),

      accrualPeriod: yup.string().required('Accrual period is required'),

      salariedDeductionsRequestMadeAvailableConfig: yup
        .string()
        .required('Made Available Time for salaried workers is required'),

      nonSalariedDeductionsRequestMadeAvailableConfig: yup
        .string()
        .required('Made Available Time for non-salaried workers is required'),
    })
    .test(
      'on-demand-not-higher-than-global',
      'On-demand pay sub-limit cannot be higher than Global limit',
      function validateOnDemandNotHigherThanGlobal(values) {
        const {
          globalAllowancePercentage = 0,
          customGlobalAllowancePercentage = 0,
          odpAllowancePercentage = 0,
          customOdpAllowancePercentage = 0,
        } = values;

        if (!isReserveEnabled || !isOnDemandPayEnabled) return true;

        // Calculate the actual on-demand sub-limit value
        let actualOnDemandValue = 0;
        let activeOdpField = 'odpAllowancePercentage';
        if (odpAllowancePercentage === 200) {
          // Custom on-demand value
          actualOnDemandValue = customOdpAllowancePercentage ?? 0;
          activeOdpField = 'customOdpAllowancePercentage';
        } else {
          // Preset on-demand value
          actualOnDemandValue = odpAllowancePercentage ?? 0;
        }

        // Calculate the actual global limit value
        let actualGlobalValue = 0;
        if (globalAllowancePercentage === 200) {
          // Custom global value
          actualGlobalValue = customGlobalAllowancePercentage ?? 0;
        } else {
          // Preset global value
          actualGlobalValue = globalAllowancePercentage ?? 0;
        }

        // Check if on-demand is higher than global limit
        if (actualOnDemandValue > actualGlobalValue) {
          return this.createError({
            path: activeOdpField, // attach to odpAllowancePercentage
            message: 'On-demand pay sub-limit cannot be higher than Global limit',
          });
        }
        return true;
      }
    );
